import React, { useState } from 'react';
import { X, Zap, Hand, Clock, Search, ChevronDown, Calendar } from 'lucide-react';

interface CreateWorkflowModalProps {
    isOpen: boolean;
    onClose: () => void;
    onContinue: (data: WorkflowFormData) => void;
}

type TriggerType = 'automatic' | 'manual' | 'timer' | 'ai';

interface WorkflowFormData {
    triggerType: TriggerType;
    workflowName?: string;
    // Automatic
    triggerActivity?: string;
    // Manual
    workflowTitle?: string;
    workflowDescription?: string;
    // AI
    aiPrompt?: string;
    // Timer
    startDate?: string;
    startTime?: string;
    repeatEvery?: number;
    frequency?: string;
    repeatOn?: string[];
    ends?: 'never' | 'on' | 'after';
    endDate?: string;
    endAfterOccurrences?: number;
}

const topActivities = [
    {
        id: '1',
        name: 'Retrieve document from google drive',
        provider: 'Google Drive',
        icon: '📄',
        iconBg: 'bg-gradient-to-br from-yellow-400 via-green-500 to-blue-500'
    },
    {
        id: '2',
        name: 'Upload document to google drive',
        provider: 'Google Drive',
        icon: '📤',
        iconBg: 'bg-gradient-to-br from-yellow-400 via-green-500 to-blue-500'
    },
    {
        id: '3',
        name: 'Create google form',
        provider: 'Google Drive',
        icon: '📋',
        iconBg: 'bg-purple-500'
    },
    {
        id: '4',
        name: 'Receive document from email',
        provider: 'Email',
        icon: '📧',
        iconBg: 'bg-gradient-to-br from-purple-500 to-indigo-600'
    }
];

const weekDays = [
    { key: 'S', label: 'Sunday' },
    { key: 'M', label: 'Monday' },
    { key: 'T', label: 'Tuesday' },
    { key: 'W', label: 'Wednesday' },
    { key: 'T2', label: 'Thursday', display: 'T' },
    { key: 'F', label: 'Friday' },
    { key: 'S2', label: 'Saturday', display: 'S' }
];

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({
    isOpen,
    onClose,
    onContinue
}) => {
    const [triggerType, setTriggerType] = useState<TriggerType>('manual');
    const [workflowName, setWorkflowName] = useState('');
    const [selectedActivity, setSelectedActivity] = useState('');
    const [workflowTitle, setWorkflowTitle] = useState('');
    const [workflowDescription, setWorkflowDescription] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [startDate, setStartDate] = useState('11/10/2023');
    const [startTime, setStartTime] = useState('01:15 PM');
    const [repeatEvery, setRepeatEvery] = useState(1);
    const [frequency, setFrequency] = useState('Week');
    const [repeatOn, setRepeatOn] = useState<string[]>(['S', 'M', 'T', 'W', 'T2', 'F', 'S2']);
    const [ends, setEnds] = useState<'never' | 'on' | 'after'>('never');
    const [searchQuery, setSearchQuery] = useState('');

    const handleClose = () => {
        // Reset form
        setTriggerType('manual');
        setWorkflowName('');
        setSelectedActivity('');
        setWorkflowTitle('');
        setWorkflowDescription('');
        setAiPrompt('');
        setStartDate('11/10/2023');
        setStartTime('01:15 PM');
        setRepeatEvery(1);
        setFrequency('Week');
        setRepeatOn(['S', 'M', 'T', 'W', 'T2', 'F', 'S2']);
        setEnds('never');
        setSearchQuery('');
        onClose();
    };

    const handleContinue = () => {
        const formData: WorkflowFormData = {
            triggerType,
            workflowName,
            triggerActivity: selectedActivity,
            workflowTitle,
            workflowDescription,
            aiPrompt,
            startDate,
            startTime,
            repeatEvery,
            frequency,
            repeatOn,
            ends
        };
        onContinue(formData);
        handleClose();
    };

    const toggleRepeatDay = (day: string) => {
        setRepeatOn(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    };

    const getTriggerInfo = () => {
        switch (triggerType) {
            case 'automatic':
                return {
                    title: 'Automatic',
                    description: 'Workflow automatically starts when predefined activity is met. This is ideal for tasks that requires urgent action.'
                };
            case 'manual':
                return {
                    title: 'Manual',
                    description: 'Workflow initiates when you choose to start it by the click of a button. This is ideal for on-demand tasks.'
                };
            case 'timer':
                return {
                    title: 'Timer',
                    description: 'Workflow starts at a specified time or at regular intervals that you set. This is ideal for scheduled tasks.'
                };
            case 'ai':
                return {
                    title: 'AI Generated',
                    description: 'Describe your workflow in natural language and let AI build the initial structure for you. Ideal for quick prototyping.'
                };
        }
    };

    if (!isOpen) return null;

    const triggerInfo = getTriggerInfo();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                    <h3 className="text-xl font-semibold text-gray-900">Create workflow</h3>
                    <div className="flex items-center gap-4">
                        <button className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm">
                            <div className="w-5 h-5 rounded bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                                <span className="text-white text-xs">✦</span>
                            </div>
                            Explore templates
                        </button>
                        <button
                            onClick={handleClose}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>
                </div>

                <div className="flex">
                    {/* Left Panel - Form */}
                    <div className="flex-1 px-6 pb-6">
                        {/* Workflow Name */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                Workflow name
                            </label>
                            <input
                                type="text"
                                value={workflowName}
                                onChange={(e) => setWorkflowName(e.target.value)}
                                placeholder="Enter workflow name"
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        {/* Trigger Type Tabs */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-600 mb-3">
                                Trigger start type
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setTriggerType('manual')}
                                    className={`flex flex-col items-center justify-center px-4 py-3 rounded-lg border-2 transition-all flex-1 ${triggerType === 'manual'
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                >
                                    <Hand size={20} className={triggerType === 'manual' ? 'text-indigo-600' : 'text-gray-500'} />
                                    <span className={`text-sm mt-1 font-medium ${triggerType === 'manual' ? 'text-indigo-700' : 'text-gray-700'}`}>
                                        Manual
                                    </span>
                                </button>
                                <button
                                    onClick={() => setTriggerType('automatic')}
                                    className={`flex flex-col items-center justify-center px-4 py-3 rounded-lg border-2 transition-all flex-1 ${triggerType === 'automatic'
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                >
                                    <Zap size={20} className={triggerType === 'automatic' ? 'text-indigo-600' : 'text-gray-500'} />
                                    <span className={`text-sm mt-1 font-medium ${triggerType === 'automatic' ? 'text-indigo-700' : 'text-gray-700'}`}>
                                        Automatic
                                    </span>
                                </button>
                                <button
                                    onClick={() => setTriggerType('timer')}
                                    className={`flex flex-col items-center justify-center px-4 py-3 rounded-lg border-2 transition-all flex-1 ${triggerType === 'timer'
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                >
                                    <Clock size={20} className={triggerType === 'timer' ? 'text-indigo-600' : 'text-gray-500'} />
                                    <span className={`text-sm mt-1 font-medium ${triggerType === 'timer' ? 'text-indigo-700' : 'text-gray-700'}`}>
                                        Timer
                                    </span>
                                </button>
                                <button
                                    onClick={() => setTriggerType('ai')}
                                    className={`flex flex-col items-center justify-center px-4 py-3 rounded-lg border-2 transition-all flex-1 ${triggerType === 'ai'
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                        }`}
                                >
                                    <div className="w-5 h-5 flex items-center justify-center">
                                        <span className={`text-lg leading-none ${triggerType === 'ai' ? 'text-indigo-600' : 'text-gray-500'}`}>✦</span>
                                    </div>
                                    <span className={`text-sm mt-1 font-medium ${triggerType === 'ai' ? 'text-indigo-700' : 'text-gray-700'}`}>
                                        AI
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Automatic Tab Content */}
                        {triggerType === 'automatic' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Select a trigger activity
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={selectedActivity}
                                            onChange={(e) => setSelectedActivity(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
                                        >
                                            <option value="">Select</option>
                                            {topActivities.map(activity => (
                                                <option key={activity.id} value={activity.id}>
                                                    {activity.name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search for connectors, activities..."
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Top activities</h4>
                                    <div className="space-y-2">
                                        {topActivities.map(activity => (
                                            <button
                                                key={activity.id}
                                                onClick={() => setSelectedActivity(activity.id)}
                                                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${selectedActivity === activity.id
                                                    ? 'border-indigo-500 bg-indigo-50'
                                                    : 'border-gray-200 hover:border-gray-300 bg-white'
                                                    }`}
                                            >
                                                <div className={`w-8 h-8 ${activity.iconBg} rounded-lg flex items-center justify-center text-white text-sm`}>
                                                    {activity.icon}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{activity.name}</div>
                                                    <div className="text-xs text-gray-500">{activity.provider}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Manual Tab Content */}
                        {triggerType === 'manual' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Workflow description <span className="text-gray-400">(Optional)</span>
                                    </label>
                                    <textarea
                                        value={workflowDescription}
                                        onChange={(e) => setWorkflowDescription(e.target.value)}
                                        placeholder="A simple workflow to send automated emails"
                                        rows={4}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Timer Tab Content */}
                        {triggerType === 'timer' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-2">
                                            Start date
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                            <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-2">
                                            Start time
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={startTime}
                                                onChange={(e) => setStartTime(e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
                                            >
                                                <option value="01:15 PM">01:15 PM</option>
                                                <option value="01:30 PM">01:30 PM</option>
                                                <option value="02:00 PM">02:00 PM</option>
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-2">
                                            Repeat every
                                        </label>
                                        <input
                                            type="number"
                                            value={repeatEvery}
                                            onChange={(e) => setRepeatEvery(parseInt(e.target.value))}
                                            min={1}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-2">
                                            Frequency
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={frequency}
                                                onChange={(e) => setFrequency(e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
                                            >
                                                <option value="Day">Day</option>
                                                <option value="Week">Week</option>
                                                <option value="Month">Month</option>
                                                <option value="Year">Year</option>
                                            </select>
                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Repeat on
                                    </label>
                                    <div className="flex gap-2">
                                        {weekDays.map((day) => (
                                            <button
                                                key={day.key}
                                                onClick={() => toggleRepeatDay(day.key)}
                                                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all ${repeatOn.includes(day.key)
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                                                    }`}
                                            >
                                                {day.display || day.key}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Ends
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            id="never"
                                            name="ends"
                                            checked={ends === 'never'}
                                            onChange={() => setEnds('never')}
                                            className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                        />
                                        <label htmlFor="never" className="text-sm text-gray-700">Never</label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* AI Tab Content */}
                        {triggerType === 'ai' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">
                                        Describe your workflow
                                    </label>
                                    <textarea
                                        value={aiPrompt}
                                        onChange={(e) => setAiPrompt(e.target.value)}
                                        placeholder="E.g. Create a workflow that checks my email every morning for invoices, extracts the amount and date, adds it to a google sheet, and sends me a summary on Slack."
                                        rows={6}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">
                                        Our AI will analyze your request and build a complete BPMN workflow structure for you to customize.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="flex justify-between items-center mt-6 pt-4">
                            <button
                                onClick={handleClose}
                                className="px-6 py-2.5 border border-indigo-600 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleContinue}
                                className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                            >
                                {triggerType === 'ai' ? 'Generate with AI' : 'Continue to editor'}
                            </button>
                        </div>
                    </div>

                    {/* Right Panel - Info */}
                    <div className="w-64 bg-gray-50 p-6 border-l border-gray-200 flex flex-col">
                        {/* Illustration placeholder */}
                        <div className="flex-1 flex items-center justify-center mb-4">
                            <div className="relative w-32 h-32">
                                {triggerType === 'automatic' && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                                                <Zap size={28} className="text-indigo-500" />
                                            </div>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                <Zap size={12} className="text-white" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {triggerType === 'manual' && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                                                <Hand size={28} className="text-indigo-500" />
                                            </div>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                <Hand size={12} className="text-white" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {triggerType === 'timer' && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                                                <Clock size={28} className="text-indigo-500" />
                                            </div>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                <Clock size={12} className="text-white" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {triggerType === 'ai' && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="relative">
                                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                                                <span className="text-2xl">✦</span>
                                            </div>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                <span className="text-xs text-white">AI</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Trigger Info */}
                        <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{triggerInfo.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{triggerInfo.description}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateWorkflowModal;
